import { useState } from 'react';
import FileDownload from 'js-file-download';
import { DocumentReviewApi } from '@apply/api';
import { ProgressSpinner } from '@apply/components';
import { SectionHeader } from '../common/SharedComponents';
import { applicationInsights } from '../ApplicationInsights';
import TranscriptReviewInputForm, { TaxYearReview } from './common/components/TranscriptReviewInputForm';
import TranscriptReviewResults from './common/components/TranscriptReviewResults';
import { useInterval } from '../common/hooks/useInterval';

interface DocumentReviewsToPoll {
    documentReviewId: string;
    taxYear: number;
}

export const TranscriptReviewLandingPage: React.FC = () => {
    const [responses, set_responses] = useState({} as DocumentReviewApi.ITaxReviewResultForYear);
    const [documentReviewsToPoll, set_documentReviewsToPoll] = useState([] as DocumentReviewsToPoll[]);

    const pollingInterval = 15000;
    const documentReviewApi = new DocumentReviewApi.DocumentReviewApi(applicationInsights);

    const loading = documentReviewsToPoll.length > 0;

    const styles = {
        informationNote: { color: '#777' },
    };

    useInterval(async () => {
        const toUpdate = {} as DocumentReviewApi.ITaxReviewResultForYear;
        for (const review of documentReviewsToPoll) {
            if (responses[review.taxYear.toString()]) {
                // Already have results for this tax year, skip polling
                continue;
            }

            const resp = await documentReviewApi.pollTaxDocumentResults(review.documentReviewId);
            if (resp?.result?.result?.lineItems?.length) {
                toUpdate[review.taxYear.toString()] = resp.result.result;
            }
        }

        const toUpdateKeys = Object.keys(toUpdate);
        if (toUpdateKeys.length) {
            set_responses((prevResponses) => ({
                ...prevResponses,
                ...toUpdate,
            }));

            set_documentReviewsToPoll((prevReviews) =>
                prevReviews.filter((r) => !toUpdateKeys.map((x) => Number(x)).includes(r.taxYear))
            );
        }
    }, pollingInterval);

    const handleSubmit = async (taxDocuments: TaxYearReview[]) => {
        set_responses({});

        await Promise.all(
            taxDocuments.map(async (taxYear) => {
                const resp = await documentReviewApi.reviewTaxDocuments(taxYear.returnFile, taxYear.transcriptFile);
                set_documentReviewsToPoll((prev) => [...prev, { documentReviewId: resp.result, taxYear: taxYear.year }]);
            })
        );
    };

    const handleDownload = async (taxYears: string[]) => {
        try {
            const fileName = 'TranscriptReview';
            const worksheets = taxYears.map((x) => ({
                worksheetName: x,
                lineItems: responses[x].lineItems,
            }));
            documentReviewApi.generateReport(worksheets).then((resp: any) => {
                FileDownload(resp, fileName + '.xlsx');
            });
        } catch (error) {
            console.error('Download failed:', error);
        }
    };

    return (
        <>
            <SectionHeader title='Transcript Review' />
            <div
                className='main-content'
                style={{ padding: '0', backgroundColor: '#f4f4f4', height: '100%' }}
            >
                <div className='scrollable-content'>
                    <TranscriptReviewInputForm onSubmit={handleSubmit} />

                    {!loading && (
                        <TranscriptReviewResults
                            responses={responses}
                            handleDownload={handleDownload}
                        />
                    )}

                    {loading && (
                        <>
                            <div style={styles.informationNote}>
                                <p>
                                    <em>Note:</em> &nbsp; Depending on the number of documents and their size, the review process
                                    may take several minutes. Please be patient after submitting.
                                </p>
                            </div>
                            <div
                                className='overlay'
                                data-testid='document-review-page-loading'
                            >
                                <div className='overlay__inner'>
                                    <div className='overlay__content'>
                                        <ProgressSpinner />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default TranscriptReviewLandingPage;
